import React from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
} from 'react-router-dom'

import { ApplicationTemplate } from './ApplicationTemplate'
import { Welcome } from './Welcome'
import { LoadingFallback } from './LoadingFallback'
import { SsoRedirect } from './auth/SsoRedirect'
import { RegionList } from './region/RegionListModern'
import { ClientList } from './client/ClientListModern'
import { ClientDetailPage } from './client/ClientDetailPage'
import { EventList } from './event/EventListModern'
import { EventConfigurationModern } from './event/EventConfigurationModern'
import { EventBasicInfo } from './event/config/EventBasicInfo'
import { BadgeConfiguration } from './event/config/BadgeConfiguration'
import { AccreditationLevels } from './event/config/AccreditationLevels'
import { Roles } from './event/config/Roles'
import { ScheduleConfig } from './event/config/ScheduleConfig'
import { GenericIdentities } from './event/config/GenericIdentities'
import { IdentityList } from './identity/IdentityListModern'
import { IdentityProfileModern } from './identity/IdentityProfileModern'
import { IdentityImportPage } from './identity/IdentityImportPage'
import { QuickProvisioningModern } from './event/QuickProvisioningModern'
import { ScheduleSetupWizard } from './event/setup/ScheduleSetupWizard'
import { EventScheduleModern } from './event/EventScheduleModern'
import { ReportRoutes } from './report/ReportRoutes'
import { BadgeTemplatesPage } from './admin/BadgeTemplatesModern'
import { BadgeBuilder } from './admin/BadgeBuilder'
import { SupportRecipientsPage } from './admin/SupportRecipientsPage'
import { SupportTicketsPage } from './admin/SupportTicketsPage'
import { MaintenancePage } from './admin/MaintenancePage'
import { SupportTicketPage } from './support/SupportTicketPage'

import { CneAccreditation } from './public/CneAccreditation'
import {
  useSessionStore,
  startSessionWatch,
  isPublicRoute,
} from '../stores/useSessionStore'
import { Toaster } from 'sonner'

import './ApplicationWidget.css'

const EventConfigurationWrapper = () => {
  return (
    <EventConfigurationModern>
      <Routes>
        <Route index element={<EventBasicInfo />} />
        <Route path="info" element={<EventBasicInfo />} />
        <Route
          path="accreditation-levels"
          element={<AccreditationLevels />}
        />
        <Route path="roles" element={<Roles />} />
        <Route path="schedule-config" element={<ScheduleConfig />} />
        <Route path="generic-identities" element={<GenericIdentities />} />
        <Route 
          path="badges" 
          element={<BadgeConfiguration />} 
        />
      </Routes>
    </EventConfigurationModern>
  )
}



const LoggedInRoutes = () => (
  <ApplicationTemplate>
    <Routes>
      <Route path="/" element={<Welcome />} />
      <Route path="/region" element={<RegionList />} />
      <Route path="/client/new" element={<ClientDetailPage />} />
      <Route path="/client/:clientId" element={<ClientDetailPage />} />
      <Route path="/client" element={<ClientList />} />
      <Route path="/identity" element={<IdentityList />} />
      <Route path="/identity/import" element={<IdentityImportPage />} />
      <Route path="/identity/:identityId" element={<IdentityProfileModern />} />
      <Route path="/event" element={<EventList />} />
      <Route
        path="/event/:eventId/config/*"
        element={<EventConfigurationWrapper />}
      />
      <Route
        path="/event/:eventId/setup/:step?"
        element={<ScheduleSetupWizard />}
      />
      <Route
        path="/event/:eventId/provision"
        element={<QuickProvisioningModern />}
      />
      <Route
        path="/event/:eventId/schedule/:dialogDirective?"
        element={<EventScheduleModern />}
      />
      <Route path="/report/*" element={<ReportRoutes />} />
      <Route path="/admin/badge-templates" element={<BadgeTemplatesPage />} />
      <Route path="/admin/badge-templates/new" element={<BadgeBuilder />} />
      <Route path="/admin/badge-templates/:templateId/edit" element={<BadgeBuilder />} />
      <Route path="/admin/support-recipients" element={<SupportRecipientsPage />} />
      <Route path="/admin/support-tickets" element={<SupportTicketsPage />} />
      <Route path="/admin/maintenance" element={<MaintenancePage />} />
      <Route path="/support" element={<SupportTicketPage />} />
    </Routes>
  </ApplicationTemplate>
)

export const ApplicationWidget: React.FC = () => {
  console.log('ApplicationWidget rendering')
  
  const { loggedIn, onLoad } = useSessionStore()
  console.log('Session store loaded, loggedIn:', loggedIn)

  React.useEffect(() => {
    console.log('Running onLoad effect')
    onLoad()
  }, [onLoad])

  // Keep this tab in step with the auth cookie: a logout in another tab (or a login
  // as a different user) must not leave this one showing the previous session.
  React.useEffect(() => startSessionWatch(), [])

  // Public route: rendered for anonymous visitors, who must not be sent to the SSO.
  // Matches the deployed /ems/ base path as well as the bare path.
  if (isPublicRoute()) {
    return <CneAccreditation />
  }

  console.log('Rendering main app, loggedIn:', loggedIn)
  return (
    <Router>
      <div className="min-h-screen bg-background text-foreground">
        {loggedIn ? <LoggedInRoutes /> : <SsoRedirect />}
        <Toaster position="top-right" richColors />
      </div>
    </Router>
  )
}
